// --- FILE: apps/website/src/components/modals/ProjectEditorModal.tsx ---

import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, TextField, TextareaField, SelectField } from '@dopomogai/ui';
import type { Project } from '@dopomogai/types';

interface ProjectEditorModalProps {
  isOpen: boolean;
  projectData: Project | null;
  isSaving: boolean;
  onClose: () => void;
  onSave: (project: Project) => void;
  onDataChange: (data: Partial<Omit<Project, 'id'>>) => void;
}

export const ProjectEditorModal: React.FC<ProjectEditorModalProps> = ({
  isOpen,
  projectData,
  isSaving,
  onClose,
  onSave,
  onDataChange,
}) => {
  const { t } = useTranslation(['admin_projects', 'common']);
  const modalContentRef = useRef<HTMLDivElement>(null);

  if (!projectData) {
    return null;
  }
  
  const isNewProject = !projectData.id;
  const modalTitle = isNewProject 
    ? t('editor.newProjectTitle') 
    : t('editor.editProjectTitle');

  const handleSave = () => {
    if (!projectData.name.trim()) {
        alert(t('editor.nameRequiredError')); 
        return;
    }
    onSave(projectData);
  };

  const footer = (
    <div className="flex justify-end gap-3">
      <Button textKey="common:cancel" variant="secondary" onClick={onClose} />
      <Button
        textKey={isNewProject ? 'common:create' : 'common:saveChanges'}
        onClick={handleSave}
        loading={isSaving}
        data-modal-primary-action="true"
      />
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      footer={footer}
      size="xl" 
    >
        <div className="space-y-6 p-1" ref={modalContentRef}>
            <TextField
                label={t('editor.nameLabel')}
                value={projectData.name}
                onChange={(e) => onDataChange({ name: e.target.value })}
                placeholder={t('editor.namePlaceholder')}
                required
            />
            <TextareaField
                label={t('editor.descriptionLabel')}
                value={projectData.description || ''}
                onChange={(e) => onDataChange({ description: e.target.value })}
                placeholder={t('editor.descriptionPlaceholder')}
                rows={4}
            />
            <SelectField
                label={t('editor.statusLabel')}
                name="project-status"
                value={projectData.status}
                onChange={(value) => onDataChange({ status: value as 'Active' | 'Archived' })}
                options={[
                    { value: 'Active', label: t('editor.statusOptions.active') },
                    { value: 'Archived', label: t('editor.statusOptions.archived') },
                ]}
                portalContainer={modalContentRef.current}
            />
        </div>
    </Modal>
  );
};